import React, { createContext, useContext } from 'react';
import Markdown from 'markdown-to-jsx';
import AiChart, { parseChartSpec } from './AiChart';

// Mientras se escribe la respuesta, un bloque ```chart a medio llegar
// todavía no es JSON válido: se muestra "armando el gráfico" en vez de error.
const StreamingContext = createContext(false);

const CHART_FENCE = /^```\s*chart\s*$/;

// Si el texto (todavía incompleto) termina adentro de un bloque de código sin
// cerrar, lo cierra para que se vea como bloque y no como texto con
// backticks. Si ese bloque es un gráfico, le saca el JSON a medias: el
// gráfico aparece entero recién cuando termina de llegar.
export function closeOpenFence(text: string): string {
  const lines = text.split('\n');
  let openAt = -1;
  lines.forEach((line, i) => {
    if (/^```/.test(line.trim())) openAt = openAt === -1 ? i : -1;
  });
  if (openAt === -1) return text;
  if (CHART_FENCE.test(lines[openAt].trim())) return `${lines.slice(0, openAt + 1).join('\n')}\n\`\`\``;
  return `${text}\n\`\`\``;
}

const ChartPlaceholder: React.FC<{ label: string }> = ({ label }) => (
  <div className="ai-chart ai-chart-placeholder">
    <span>{label}</span>
  </div>
);

const CodeBlock: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const streaming = useContext(StreamingContext);
  const child = React.Children.toArray(children)[0];
  if (React.isValidElement<{ className?: string; children?: React.ReactNode }>(child) && /\blang-chart\b/.test(child.props.className ?? '')) {
    const source = String(child.props.children ?? '').trim();
    const spec = source ? parseChartSpec(source) : null;
    if (spec) return <AiChart spec={spec} />;
    return <ChartPlaceholder label={streaming ? 'Armando el gráfico…' : 'No se pudo dibujar el gráfico.'} />;
  }
  return <pre className="ai-code">{children}</pre>;
};

// Las tablas anchas se scrollean de costado en vez de romper el layout en el celular.
const Table: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="ai-table-wrap">
    <table>{children}</table>
  </div>
);

const Link: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement>> = (props) => (
  <a {...props} target="_blank" rel="noopener noreferrer" />
);

const OPTIONS = {
  // El HTML que mande el modelo se muestra como texto, nunca se interpreta.
  disableParsingRawHTML: true,
  forceBlock: true,
  overrides: { pre: { component: CodeBlock }, table: { component: Table }, a: { component: Link } },
};

const AiMarkdown: React.FC<{ text: string; streaming?: boolean }> = ({ text, streaming = false }) => (
  <StreamingContext.Provider value={streaming}>
    <Markdown options={OPTIONS}>{streaming ? closeOpenFence(text) : text}</Markdown>
  </StreamingContext.Provider>
);

export default React.memo(AiMarkdown);
